/**
 * Single source of truth for implementation overdue + health computation.
 *
 * Used by recalcProjectRollups (persists the rollups), the cross-group
 * dashboard (computes live), and the group-detail panel header (computes live).
 * Keeping one implementation prevents the three surfaces from disagreeing about
 * whether a task is overdue or what a project's health is.
 *
 * IMPORTANT: dates are compared in the LOCAL timezone. Using UTC
 * (new Date().toISOString()) flips a task overdue a day early for users west of
 * UTC in the evening, which then disagrees with the local-time "days until"
 * display.
 */

import type { HealthRule, HealthRuleCriticality } from '@shared/entities/admin-config';
import { TERMINAL_TASK_STATUSES, HealthStatus, TaskStatus, Criticality } from './types';
import { businessDaysBetween } from './schedule';

/** Checklist completion over a set of LEAF task statuses. */
export interface TaskCompletion {
  /** Leaf tasks that count toward the percentage. Waived work is NOT here. */
  scored: number;
  /** Scored tasks that are Complete. Never includes waived tasks. */
  complete: number;
  /** Leaf tasks waived as Not Needed, excluded from `scored` entirely. */
  waived: number;
  /** Every leaf task seen, scored + waived. For "9 of 10, 1 waived" copy. */
  total: number;
  /** 0-100, rounded. See the all-waived and no-tasks rules below. */
  pct: number;
}

/**
 * Checklist completion with waived work removed from the DENOMINATOR
 * (ADO #1243, decided by Mark and Lindsay 2026-07-28).
 *
 * The bug this replaces: a Not Needed task was counted as COMPLETE while still
 * sitting in the total, because both statuses live in TERMINAL_TASK_STATUSES.
 * So 9 done + 1 waived read as 10 of 10, and so did 5 done + 5 waived. A waived
 * task was being scored as an accomplishment. Lindsay's framing settled it: a
 * waived task is out of scope for the project, which is not the same as done.
 *
 * The rule: waived tasks leave the denominator. 9 done + 1 waived is 9 of 9.
 *
 * TERMINAL_TASK_STATUSES deliberately still contains both, because "needs no
 * further action" remains the right question for overdue detection and for
 * open-task lists. It is only the PERCENTAGE that must tell them apart, which
 * is why this lives in one function rather than by editing that set.
 *
 * TWO EDGE CASES, both deliberate:
 *  - Every leaf waived: 100. Nothing is outstanding, so the section or project
 *    is done. Returning 0 would strand a legitimately-waived section at zero
 *    forever with no action available to fix it, and this number feeds project
 *    health and the Operation Daylight lit gate.
 *  - No leaf tasks at all: 0, preserving today's behaviour. An empty project is
 *    not a finished one, it is one with no plan yet.
 */
export function computeTaskCompletion(statuses: Iterable<number>): TaskCompletion {
  let total = 0;
  let waived = 0;
  let complete = 0;
  for (const s of statuses) {
    total++;
    if (s === TaskStatus.NOT_NEEDED) waived++;
    else if (s === TaskStatus.COMPLETE) complete++;
  }
  const scored = total - waived;
  const pct = scored > 0 ? Math.round((complete / scored) * 100) : total > 0 ? 100 : 0;
  return { scored, complete, waived, total, pct };
}

/** Today's date as YYYY-MM-DD in the local timezone. */
export function localTodayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** A task is overdue if it's past its target end (date-only) and not terminal. */
export function isTaskOverdue(
  task: { status: number; targetEnd?: string },
  todayStr: string = localTodayStr(),
): boolean {
  if (TERMINAL_TASK_STATUSES.has(task.status)) return false;
  if (!task.targetEnd) return false;
  return task.targetEnd.split('T')[0]! < todayStr;
}

/**
 * A parent task's status rolled up from its direct children (7/23 review
 * E2/E11): all children terminal (Complete / Not Needed) -> Complete; any child
 * started (anything past Not Started) -> In Progress; otherwise Not Started.
 * A parent with subtasks is a container, so its status is derived, never the
 * driver of the checklist %.
 */
export function deriveParentStatus(childStatuses: number[]): number {
  if (childStatuses.length === 0) return TaskStatus.NOT_STARTED;
  // ADO #1278: a parent whose children are EVERY ONE waived is itself waived,
  // not complete. Rolling it up to Complete would strike out the children while
  // the parent row claimed the section was accomplished, and would put the
  // parent back into the scored denominator that its children just left.
  if (childStatuses.every((s) => s === TaskStatus.NOT_NEEDED)) return TaskStatus.NOT_NEEDED;
  if (childStatuses.every((s) => TERMINAL_TASK_STATUSES.has(s))) return TaskStatus.COMPLETE;
  if (childStatuses.some((s) => s !== TaskStatus.NOT_STARTED)) return TaskStatus.IN_PROGRESS;
  return TaskStatus.NOT_STARTED;
}

/** An overdue task as health sees it: how critical, how late. */
export interface OverdueTask {
  criticality?: number;
  targetEnd?: string;
}

const CRITICALITY_KEY: Record<number, HealthRuleCriticality> = {
  [Criticality.STANDARD]: 'standard',
  [Criticality.IMPORTANT]: 'important',
  [Criticality.SHOWSTOPPER]: 'showstopper',
};

/**
 * Business days an overdue task is late: weekdays after its target date up to
 * today, and at least 1, so a task that went overdue over a weekend counts as
 * 1 day late rather than 0.
 */
export function businessDaysLate(targetEnd: string, todayStr: string = localTodayStr()): number {
  return Math.max(1, businessDaysBetween(targetEnd.split('T')[0]!, todayStr));
}

/**
 * Project health from its OVERDUE tasks and the configurable rules
 * (ADO #1725, implementation.healthRules in Catalog). Each overdue task is
 * scored by its criticality's row: red once it is `redAfterBusinessDays` late,
 * yellow once it is `yellowAfterBusinessDays` late, null meaning never. The
 * project takes its worst task.
 *
 * The group's effective date plays no part. The rule this replaces turned any
 * overdue task red on the effective date, which flagged every 1st-of-month
 * group at go-live while post-go-live work was legitimately still open.
 * A task with no criticality reads as Standard. A coordinator's manual
 * healthOverride is applied by the caller at read time.
 */
export function computeProjectHealth(params: {
  overdueTasks: Iterable<OverdueTask>;
  rules: HealthRule[];
  todayStr?: string;
}): number {
  const todayStr = params.todayStr ?? localTodayStr();
  const byKey = new Map(params.rules.map((r) => [r.criticality, r]));
  let worst: number = HealthStatus.GREEN;
  for (const t of params.overdueTasks) {
    if (!t.targetEnd) continue;
    const rule = byKey.get(CRITICALITY_KEY[t.criticality ?? Criticality.STANDARD] ?? 'standard');
    if (!rule) continue;
    const late = businessDaysLate(t.targetEnd, todayStr);
    if (rule.redAfterBusinessDays != null && late >= rule.redAfterBusinessDays) return HealthStatus.RED;
    if (rule.yellowAfterBusinessDays != null && late >= rule.yellowAfterBusinessDays) worst = HealthStatus.YELLOW;
  }
  return worst;
}
